import React, { useState, useRef, useEffect } from "react";
import { DocumentEditor } from "@/components/documents";
import { formatTimeAgo } from "@/utils/datetime";
import DocumentRichTextToolbar from "@/components/documents/DocumentRichTextToolbar";
import { Button } from "@/components/ui/button/Button";
import { DestructiveTrashIconButton } from "@/components/ui/DestructiveTrashIconButton";
import { ConfirmDeleteDialog } from "@/components/ui/dialog/ConfirmDeleteDialog";
import DatePicker from "@/components/ui/date-picker";
import Drawer from "@/components/ui/drawer";
import type { DocumentContent } from "@/types/documents";
import {
  createCloudDocument,
  updateCloudDocument,
  deleteCloudDocument,
} from "@/services/api/cloud-documents";
import type { Document } from "@/services/api/documents/types";
import { format } from "date-fns";
import { ImagesAPI } from "@/services/api";
import { faTrashAlt } from "@awesome.me/kit-70c1f0055f/icons/classic/light";

// Custom styles to remove focus outlines
const customStyles = `
  .no-focus-outline * {
    outline: none !important;
  }
  
  .ProseMirror {
    outline: none !important;
  }
  
  .ProseMirror-focused {
    outline: none !important;
    box-shadow: none !important;
  }
  
  .ProseMirror:focus {
    outline: none !important;
  }
  
  /* Table styles */
  .ProseMirror table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 0;
    overflow: hidden;
  }

  .ProseMirror table td,
  .ProseMirror table th {
    min-width: 1em;
    border: 1px solid #f1f5f9;
    padding: 8px;
    vertical-align: top;
    box-sizing: border-box;
    position: relative;
  }

  .ProseMirror table th {
    font-weight: bold;
    background-color: #f8fafc;
  }

  .dark .ProseMirror table td,
  .dark .ProseMirror table th {
    border-color: #374151;
  }

  .dark .ProseMirror table th {
    background-color: #1f2937;
  }

  .ProseMirror table .selectedCell:after {
    z-index: 2;
    position: absolute;
    content: "";
    left: 0; right: 0; top: 0; bottom: 0;
    background: rgba(200, 200, 255, 0.4);
    pointer-events: none;
  }

  /* Column resize handle styles */
  .ProseMirror table .column-resize-handle {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: -2px;
    width: 4px;
    background-color: #adf;
    pointer-events: none;
  }

  .ProseMirror table p {
    margin: 0;
  }

  .dark .ProseMirror {
    color: #e5e7eb;
  }

  /* Add style for selected image */
  img.ProseMirror-selectednode {
    outline: 3px solid #615fff;
    box-shadow: 0 0 0 3px #615fff;
    border-radius: 0.25rem;
  }
`;

interface DocumentEditorDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  initialContent?: DocumentContent;
  onSaveComplete?: (document: Document) => void;
  document?: Document;
  documentId?: string;
  resourceMetadata?: {
    resource_type: string;
    resource_id: string;
  };
  isWindowMode?: boolean;
}

export function DocumentEditorDrawer({
  isOpen,
  onClose,
  initialContent,
  onSaveComplete,
  document,
  documentId,
  resourceMetadata,
  isWindowMode = false,
}: DocumentEditorDrawerProps) {
  const [content, setContent] = useState("");
  const [landscapeMode, setLandscapeMode] = useState(
    document?.landscape ?? false
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingDoc, setEditingDoc] = useState<{
    name: string;
    asAtDate: string;
  }>({
    name: document?.filename || "New Document",
    asAtDate: document?.as_at || format(new Date(), "yyyy-MM-dd"),
  });
  const editorRef = useRef<any>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [editorInstance, setEditorInstance] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Update editingDoc and landscape mode when document changes
  useEffect(() => {
    if (document) {
      setEditingDoc({
        name: document.filename || "Untitled",
        asAtDate:
          document.as_at ||
          (document.upload_date ? format(new Date(document.upload_date), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")),
      });
      setLandscapeMode(document.landscape ?? false);
    }
  }, [document]);

  // Focus title input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditingTitle]);

  // Effect to set initial content when the drawer opens or in window mode
  useEffect(() => {
    if ((isOpen || isWindowMode) && initialContent && editorInstance) {
      // Use the editor's setContent method to properly set the content
      editorInstance.commands.setContent(initialContent);
      // Sync the drawer's HTML 'content' state with what's now in the editor
      const currentHtmlInEditor = editorInstance.getHTML();
      setContent(currentHtmlInEditor); // Update HTML state immediately
    }
  }, [isOpen, isWindowMode, initialContent, editorInstance, setContent]); // Added setContent dependency

  // Effect to capture the editor instance once it's available
  useEffect(() => {
    if (!isOpen && !isWindowMode) {
      setEditorInstance(null);
      return;
    }

    // Small delay to ensure the editor is mounted
    const timer = setTimeout(() => {
      if (editorRef.current?.editor) {
        setEditorInstance(editorRef.current.editor);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [isOpen, isWindowMode]);

  // Example document object
  const doc = {
    id: "example-doc-1",
    name: "Example Document",
    timestamp: new Date().toISOString(),
    owner: "You",
  };

  // Wrapper for image upload to return only the URL string
  const handleImageUpload = async (file: File): Promise<string> => {
    try {
      const response = await ImagesAPI.uploadImage(file);
      const uploadedImage = response.data?.[0];
      if (uploadedImage?.url) {
        return uploadedImage.url;
      } else {
        throw new Error("Image URL not found in response");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      throw error; // Re-throw the error to be handled by the caller
    }
  };

  const paperStyle = landscapeMode
    ? {
        width: "1131px",
        height: "794px",
      }
    : {
        width: "800px",
        minHeight: "1123px",
      };

  const focusEditor = () => {
    if (editorRef.current && editorRef.current.editor) {
      // Only focus if there's no selection and editor is not already focused
      const selection = window.getSelection();
      const hasSelection = selection ? selection.toString().length > 0 : false;
      const editorHasFocus = editorRef.current.editor.isFocused;

      if (!hasSelection && !editorHasFocus) {
        editorRef.current.editor.commands.focus("end");
      }
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const documentData = {
        name: editingDoc.name,
        body: editorInstance.getJSON(),
        as_at: editingDoc.asAtDate,
        landscape: landscapeMode,
        ...(resourceMetadata && {
          resource_type: resourceMetadata.resource_type,
          resource_id: resourceMetadata.resource_id,
        }),
      };

      let savedDocument;
      if (document?.document_id) {
        // Update existing document
        savedDocument = await updateCloudDocument(
          document.document_id,
          documentData
        );
      } else {
        // Create new document
        savedDocument = await createCloudDocument(documentData);
      }

      if (onSaveComplete) {
        onSaveComplete(savedDocument);
      }

      onClose();
    } catch (error) {
      console.error("Error saving document:", error);
      // Here you might want to show an error message to the user
    } finally {
      setIsSaving(false);
    }
  };

  const handleLandscapeModeChange = (value: boolean) => {
    setLandscapeMode(value);
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setIsEditingTitle(false);
    }
  };

  const handleDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (document?.document_id) {
      try {
        await deleteCloudDocument(document.document_id);
        setIsDeleteDialogOpen(false);
        onClose();
        if (onSaveComplete) {
          onSaveComplete(document);
        }
      } catch (error) {
        console.error("Error deleting document:", error);
      }
    }
  };

  return (
    <>
      {isWindowMode ? (
        // Window mode - render without drawer wrapper, with rounded container
        <div className="h-full w-full flex items-center justify-center p-4">
          <div className="h-full w-full max-w-[1600px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            {/* Inject custom styles to remove focus outlines */}
            <style>{customStyles}</style>

            <div className="bg-white dark:bg-gray-800 p-4 pl-5 flex justify-between items-center w-full border-b border-gray-200 dark:border-gray-700 space-x-2 flex-shrink-0">
              <div className="flex flex-col space-y-0.5 w-full">
                <div className="text-xl flex items-center space-x-2 font-medium dark:text-white">
                  {isEditingTitle ? (
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={editingDoc.name}
                      onChange={(e) =>
                        setEditingDoc((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      onBlur={handleTitleBlur}
                      onKeyDown={handleTitleKeyDown}
                      className="bg-transparent border-none focus:outline-none focus:ring-0 w-full text-xl font-medium py-0.5 dark:text-white"
                      autoFocus
                    />
                  ) : (
                    <div
                      onClick={handleTitleClick}
                      className="cursor-text hover:bg-gray-50 dark:hover:bg-gray-700 py-0.5 rounded truncate"
                    >
                      {editingDoc.name}
                    </div>
                  )}
                </div>
                <div className="font-mono text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <div className="flex-shrink-0 min-w-fit">
                    {document
                      ? `Created ${formatTimeAgo(document.upload_date)}`
                      : "Draft"}
                  </div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    •
                  </span>
                  <DatePicker
                    mode="single"
                    value={editingDoc.asAtDate}
                    onChange={(date) => {
                      if (typeof date === "string") {
                        setEditingDoc((prev) => ({ ...prev, asAtDate: date }));
                        setIsDatePickerOpen(false);
                      }
                    }}
                    isOpen={isDatePickerOpen}
                    onOpenChange={setIsDatePickerOpen}
                    showPresets={false}
                    trigger={
                      <span className="cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 px-1 py-2 rounded flex-shrink-0">
                        As at:{" "}
                        {format(new Date(editingDoc.asAtDate), "dd MMM yyyy")}
                      </span>
                    }
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                {document?.document_id && (
                  <DestructiveTrashIconButton
                    onClick={handleDelete}
                    title="Delete document"
                    aria-label="Delete document"
                  />
                )}
                <Button color="indigo" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>

            {/* Editor toolbar positioned above the document */}
            <div className="flex justify-center bg-white dark:bg-gray-800 p-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              {editorInstance && (
                <DocumentRichTextToolbar
                  editor={editorInstance}
                  onFileUpload={handleImageUpload}
                  doc={doc}
                  landscapeMode={landscapeMode}
                  onLandscapeModeChange={handleLandscapeModeChange}
                />
              )}
            </div>

            <div className="flex flex-grow divide-x divide-gray-200 dark:divide-gray-700 min-h-0 overflow-hidden">
              {/* Main document editor area */}
              <div className="flex-grow flex flex-col min-h-0">
                <div className="flex-grow bg-gray-50 dark:bg-gray-900 p-8 overflow-auto flex justify-center">
                  <div
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-lg cursor-text h-auto flex flex-col p-14"
                    style={paperStyle}
                  >
                    <DocumentEditor
                      ref={editorRef}
                      doc={doc}
                      value={content}
                      onChange={setContent}
                      onFileUpload={handleImageUpload}
                      onInitialClick={focusEditor}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Drawer mode - original behavior
        <Drawer
          isOpen={isOpen}
          onClose={onClose}
          title="Document Editor"
          position="bottom"
        >
          <div className="bg-gray-50 dark:bg-gray-900 min-h-screen flex flex-col">
            {/* Inject custom styles to remove focus outlines */}
            <style>{customStyles}</style>

            <div className="bg-white dark:bg-gray-800 p-4 pl-5 flex justify-between items-center w-full border-b border-gray-200 dark:border-gray-700 space-x-2">
              <div className="flex flex-col space-y-0.5 w-full">
                <div className="text-xl flex items-center space-x-2 font-medium dark:text-white">
                  {isEditingTitle ? (
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={editingDoc.name}
                      onChange={(e) =>
                        setEditingDoc((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      onBlur={handleTitleBlur}
                      onKeyDown={handleTitleKeyDown}
                      className="bg-transparent border-none focus:outline-none focus:ring-0 w-full text-xl font-medium py-0.5 dark:text-white"
                      autoFocus
                    />
                  ) : (
                    <div
                      onClick={handleTitleClick}
                      className="cursor-text hover:bg-gray-50 dark:hover:bg-gray-700 py-0.5 rounded truncate"
                    >
                      {editingDoc.name}
                    </div>
                  )}
                </div>
                <div className="font-mono text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <div className="flex-shrink-0 min-w-fit">
                    {document
                      ? `Created ${formatTimeAgo(document.upload_date)}`
                      : "Draft"}
                  </div>
                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                    •
                  </span>
                  <DatePicker
                    mode="single"
                    value={editingDoc.asAtDate}
                    onChange={(date) => {
                      if (typeof date === "string") {
                        setEditingDoc((prev) => ({ ...prev, asAtDate: date }));
                        setIsDatePickerOpen(false);
                      }
                    }}
                    isOpen={isDatePickerOpen}
                    onOpenChange={setIsDatePickerOpen}
                    showPresets={false}
                    trigger={
                      <span className="cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 px-1 py-2 rounded flex-shrink-0">
                        As at:{" "}
                        {format(new Date(editingDoc.asAtDate), "dd MMM yyyy")}
                      </span>
                    }
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2 flex-shrink-0">
                {document?.document_id && (
                  <DestructiveTrashIconButton
                    onClick={handleDelete}
                    title="Delete document"
                    aria-label="Delete document"
                  />
                )}
                <Button color="indigo" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>

            {/* Editor toolbar positioned above the document */}
            <div className="flex justify-center bg-white dark:bg-gray-800 p-2 border-b border-gray-200 dark:border-gray-700">
              {editorInstance && (
                <DocumentRichTextToolbar
                  editor={editorInstance}
                  onFileUpload={handleImageUpload}
                  doc={doc}
                  landscapeMode={landscapeMode}
                  onLandscapeModeChange={handleLandscapeModeChange}
                />
              )}
            </div>

            <div className="flex flex-grow divide-x divide-gray-200 dark:divide-gray-700">
              {/* Main document editor area */}
              <div className="flex-grow flex flex-col min-h-0">
                <div className="flex-grow bg-gray-50 dark:bg-gray-900 p-8 overflow-auto flex justify-center">
                  <div
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-lg cursor-text h-auto flex flex-col p-14"
                    style={paperStyle}
                  >
                    <DocumentEditor
                      ref={editorRef}
                      doc={doc}
                      value={content}
                      onChange={setContent}
                      onFileUpload={handleImageUpload}
                      onInitialClick={focusEditor}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      )}

      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete?"
        description={
          document
            ? `You are about to delete "${(
                document.display_name?.trim() ||
                document.filename?.trim() ||
                editingDoc.name?.trim() ||
                "this document"
              )}". This action cannot be undone and will permanently remove the document.`
            : "This action cannot be undone and will permanently remove the document."
        }
        confirmLabel="Yes, Delete"
      />
    </>
  );
}
